import React, { useState } from 'react';
import './RiskProfile.css';

const RiskProfile = () => {
  const [answers, setAnswers] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [userName, setUserName] = useState('');
  const [riskProfile, setRiskProfile] = useState(null);

  React.useEffect(() => {
    const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
    setUserName(userDetails.name || 'User');
  }, []);

  const questions = [
    // SECTION A: PERSONAL & FINANCIAL INFORMATION
    {
      id: 1,
      section: 'A',
      text: 'What is your age group?',
      options: [
        { value: 'above_60', label: 'Above 60', score: 1 },
        { value: '51_60', label: '51–60', score: 2 },
        { value: '41_50', label: '41–50', score: 3 },
        { value: '30_40', label: '30–40', score: 4 },
        { value: 'below_30', label: 'Below 30', score: 5 },
      ],
    },
    {
      id: 2,
      section: 'A',
      text: 'What is your annual household income?',
      options: [
        { value: 'below_5', label: 'Below ₹5 Lakhs', score: 1 },
        { value: '5_10', label: '₹5 – ₹10 Lakhs', score: 2 },
        { value: '10_25', label: '₹10 – ₹25 Lakhs', score: 3 },
        { value: '25_50', label: '₹25 – ₹50 Lakhs', score: 4 },
        { value: 'above_50', label: 'Above ₹50 Lakhs', score: 5 },
      ],
    },
    {
      id: 3,
      section: 'A',
      text: 'What portion of your income do you regularly save/invest?',
      options: [
        { value: 'less_10', label: 'Less than 10%', score: 1 },
        { value: '10_25', label: '10% – 25%', score: 2 },
        { value: '26_50', label: '26% – 50%', score: 3 },
        { value: '51_75', label: '51% – 75%', score: 4 },
        { value: 'more_75', label: 'More than 75%', score: 5 },
      ],
    },
    {
      id: 4,
      section: 'A',
      text: 'How many financial dependents do you have (excluding yourself)?',
      options: [
        { value: 'none', label: 'None', score: 5 },
        { value: 'one', label: 'One', score: 4 },
        { value: 'two', label: 'Two', score: 3 },
        { value: 'three', label: 'Three', score: 2 },
        { value: 'more_than_three', label: 'More than Three', score: 1 },
      ],
    },
    {
      id: 5,
      section: 'A',
      text: 'What is your primary source of income?',
      options: [
        { value: 'pension', label: 'Pension', score: 1 },
        { value: 'salary', label: 'Salary', score: 2 },
        { value: 'business', label: 'Business', score: 3 },
        { value: 'rental_passive', label: 'Rental/Passive', score: 4 },
        { value: 'combination', label: 'Combination of multiple stable sources', score: 5 },
      ],
    },
    // SECTION B: INVESTMENT GOALS, EXPERIENCE & HORIZON
    {
      id: 6,
      section: 'B',
      text: 'What is your main investment objective?',
      options: [
        { value: 'capital_preservation', label: 'Capital Preservation', score: 1 },
        { value: 'regular_income', label: 'Regular Income', score: 2 },
        { value: 'capital_growth_stability', label: 'Capital Growth with Stability', score: 3 },
        { value: 'long_term_wealth', label: 'Long-Term Wealth Creation', score: 4 },
        { value: 'aggressive_growth', label: 'Aggressive Growth', score: 5 },
      ],
    },
    {
      id: 7,
      section: 'B',
      text: 'What is your typical investment horizon?',
      options: [
        { value: 'less_1_year', label: 'Less than 1 year', score: 1 },
        { value: '1_3_years', label: '1 – 3 years', score: 2 },
        { value: '3_5_years', label: '3 – 5 years', score: 3 },
        { value: '5_10_years', label: '5 – 10 years', score: 4 },
        { value: 'more_10_years', label: 'More than 10 years', score: 5 },
      ],
    },
    {
      id: 8,
      section: 'B',
      text: 'What is your level of knowledge of investment products?',
      options: [
        { value: 'none', label: 'None', score: 1 },
        { value: 'basic', label: 'Basic (FDs, PPF, Insurance)', score: 2 },
        { value: 'moderate', label: 'Moderate (Mutual Funds, NPS)', score: 3 },
        { value: 'good', label: 'Good (Equity MFs, Bonds, ETFs)', score: 4 },
        { value: 'advanced', label: 'Advanced (Direct Equity, AIFs, PMS, Derivatives)', score: 5 },
      ],
    },
    {
      id: 9,
      section: 'B',
      text: 'What type of returns do you expect annually from your investments?',
      options: [
        { value: '4_6_percent', label: '4–6%', score: 1 },
        { value: '6_8_percent', label: '6–8%', score: 2 },
        { value: '8_10_percent', label: '8–10%', score: 3 },
        { value: '10_15_percent', label: '10–15%', score: 4 },
        { value: 'above_15_percent', label: 'Above 15%', score: 5 },
      ],
    },
    {
      id: 10,
      section: 'B',
      text: 'How much of your investable surplus are you willing to allocate to market-linked instruments?',
      options: [
        { value: 'less_10_percent', label: 'Less than 10%', score: 1 },
        { value: '10_25_percent', label: '10% – 25%', score: 2 },
        { value: '26_50_percent', label: '26% – 50%', score: 3 },
        { value: '51_75_percent', label: '51% – 75%', score: 4 },
        { value: 'more_75_percent', label: 'More than 75%', score: 5 },
      ],
    },
    // SECTION C: BEHAVIORAL & RISK TOLERANCE
    {
      id: 11,
      section: 'C',
      text: 'How would you react to a 15% fall in the market value of your investments in a short period?',
      options: [
        { value: 'redeem_all', label: 'Immediately redeem all investments', score: 1 },
        { value: 'reduce_exposure', label: 'Reduce exposure to riskier products', score: 2 },
        { value: 'wait_watch', label: 'Wait and watch', score: 3 },
        { value: 'stay_invested', label: 'Stay invested', score: 4 },
        { value: 'invest_more', label: 'Invest more to average costs', score: 5 },
      ],
    },
    {
      id: 12,
      section: 'C',
      text: 'Have you faced a major financial emergency in the last 2 years impacting your savings?',
      options: [
        { value: 'yes_significantly', label: 'Yes, significantly', score: 1 },
        { value: 'yes_moderately', label: 'Yes, moderately', score: 2 },
        { value: 'minor_impact', label: 'Minor impact', score: 3 },
        { value: 'no_impact', label: 'No impact', score: 4 },
        { value: 'never_faced', label: 'Never faced such a situation', score: 5 },
      ],
    },
    {
      id: 13,
      section: 'C',
      text: 'Do you maintain a contingency (emergency) fund for at least 6 months of expenses?',
      options: [
        { value: 'no', label: 'No', score: 1 },
        { value: 'aware_not_started', label: 'Aware but haven\'t started', score: 2 },
        { value: 'planning_started', label: 'Planning/Started', score: 3 },
        { value: 'partially_covered', label: 'Yes – partially covered', score: 4 },
        { value: 'fully_covered', label: 'Yes – fully covered', score: 5 },
      ],
    },
    {
      id: 14,
      section: 'C',
      text: 'How important is liquidity in your investments?',
      options: [
        { value: 'very_important', label: 'Very important – need frequent access', score: 1 },
        { value: 'important', label: 'Important', score: 2 },
        { value: 'neutral', label: 'Neutral', score: 3 },
        { value: 'can_lock_3_5_years', label: 'Can lock-in for 3–5 years', score: 4 },
        { value: 'long_term_focused', label: 'Long-term focused – liquidity not required', score: 5 },
      ],
    },
    {
      id: 15,
      section: 'C',
      text: 'What is your tax preference for investments?',
      options: [
        { value: 'only_tax_free', label: 'Only tax-free returns (e.g., PPF, FDs below ₹40k)', score: 1 },
        { value: 'prefer_80c', label: 'Prefer Section 80C instruments only', score: 2 },
        { value: 'mix_products', label: 'Mix of taxable and tax-saving products', score: 3 },
        { value: 'open_paying_tax', label: 'Open to paying tax for better returns', score: 4 },
        { value: 'tax_not_concern', label: 'Tax is not a primary concern', score: 5 },
      ],
    },
  ];

  const handleAnswer = (score) => {
    setAnswers(prev => ({
      ...prev,
      [currentQuestion]: score
    }));

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      calculateRiskProfile();
    }
  };

  const calculateRiskProfile = () => {
    const totalScore = Object.values(answers).reduce((sum, score) => sum + score, 0);

    let profile;
    if (totalScore >= 15 && totalScore <= 29) {
      profile = {
        type: 'Conservative',
        category: 'Conservative',
        investorType: 'Capital protection, limited volatility',
        productSuitability: 'Liquid Funds, FDs, Short Debt, Tax-Free Bonds',
        score: totalScore,
        range: '15 – 29'
      };
    } else if (totalScore >= 30 && totalScore <= 44) {
      profile = {
        type: 'Moderately Conservative',
        category: 'Moderately Conservative',
        investorType: 'Income-focused with limited equity',
        productSuitability: 'Hybrid Debt, Conservative Hybrid, Balanced Advantage',
        score: totalScore,
        range: '30 – 44'
      };
    } else if (totalScore >= 45 && totalScore <= 59) {
      profile = {
        type: 'Moderate Risk',
        category: 'Moderate Risk',
        investorType: 'Growth-focused, willing to take some risk',
        productSuitability: 'Equity MFs, Index Funds, Balanced Hybrid, NPS',
        score: totalScore,
        range: '45 – 59'
      };
    } else if (totalScore >= 60 && totalScore <= 69) {
      profile = {
        type: 'Aggressive',
        category: 'Aggressive',
        investorType: 'High growth, tolerates market fluctuations',
        productSuitability: 'MFs, SIFs, PMS, ETFs, AIF',
        score: totalScore,
        range: '60 – 69'
      };
    } else if (totalScore >= 70 && totalScore <= 75) {
      profile = {
        type: 'Very Aggressive',
        category: 'Very Aggressive',
        investorType: 'Aggressive investor, long-term view',
        productSuitability: 'Equity MFs, SIFs, PMS, AIF, Thematic',
        score: totalScore,
        range: '70 – 75'
      };
    }

    setRiskProfile(profile);
    
    // Save risk profile to localStorage
    localStorage.setItem('riskProfile', JSON.stringify({
      ...profile,
      answers: answers,
      completedAt: new Date().toISOString()
    }));
  };

  const currentQ = questions[currentQuestion];
  const getSectionTitle = (section) => {
    switch(section) {
      case 'A': return 'Personal & Financial Information';
      case 'B': return 'Investment Goals, Experience & Horizon';
      case 'C': return 'Behavioral & Risk Tolerance';
      default: return '';
    }
  };

  return (
    <div className="risk-profile-container">
      <h2>Welcome, {userName}</h2>
      <h3>Risk Profile Assessment</h3>
      
      {riskProfile ? (
        <div className="result-card">
          <h3>Your Risk Profile: {riskProfile.type}</h3>
          <p className="score">Total Score: {riskProfile.score}/75</p>
          
          <div className="scoring-system">
            <h4>📊 SCORING SYSTEM</h4>
            <p><strong>Total Max Score:</strong> 75</p>
            <p><strong>Total Min Score:</strong> 15</p>
            
            <table className="scoring-table">
              <thead>
                <tr>
                  <th>Total Score</th>
                  <th>Risk Category</th>
                  <th>Investor Type</th>
                  <th>Product Suitability</th>
                </tr>
              </thead>
              <tbody>
                <tr className={riskProfile.range === '15 – 29' ? 'current-profile' : ''}>
                  <td>15 – 29</td>
                  <td>Conservative</td>
                  <td>Capital protection, limited volatility</td>
                  <td>Liquid Funds, FDs, Short Debt, Tax-Free Bonds</td>
                </tr>
                <tr className={riskProfile.range === '30 – 44' ? 'current-profile' : ''}>
                  <td>30 – 44</td>
                  <td>Moderately Conservative</td>
                  <td>Income-focused with limited equity</td>
                  <td>Hybrid Debt, Conservative Hybrid, Balanced Advantage</td>
                </tr>
                <tr className={riskProfile.range === '45 – 59' ? 'current-profile' : ''}>
                  <td>45 – 59</td>
                  <td>Moderate Risk</td>
                  <td>Growth-focused, willing to take some risk</td>
                  <td>Equity MFs, Index Funds, Balanced Hybrid, NPS</td>
                </tr>
                <tr className={riskProfile.range === '60 – 69' ? 'current-profile' : ''}>
                  <td>60 – 69</td>
                  <td>Aggressive</td>
                  <td>High growth, tolerates market fluctuations</td>
                  <td>MFs, SIFs, PMS, ETFs, AIF</td>
                </tr>
                <tr className={riskProfile.range === '70 – 75' ? 'current-profile' : ''}>
                  <td>70 – 75</td>
                  <td>Very Aggressive</td>
                  <td>Aggressive investor, long-term view</td>
                  <td>Equity MFs, SIFs, PMS, AIF, Thematic</td>
                </tr>
              </tbody>
            </table>
            
            <div className="your-profile">
              <h4>🎯 Your Profile Details:</h4>
              <p><strong>Score Range:</strong> {riskProfile.range}</p>
              <p><strong>Risk Category:</strong> {riskProfile.category}</p>
              <p><strong>Investor Type:</strong> {riskProfile.investorType}</p>
              <p><strong>Recommended Products:</strong> {riskProfile.productSuitability}</p>
            </div>
          </div>
          
          <div className="action-buttons">
            <button className="restart-button" onClick={() => {
              setAnswers({});
              setCurrentQuestion(0);
              setRiskProfile(null);
            }}>Retake Assessment</button>
            <button className="continue-button" onClick={() => {
              alert('Risk profile saved! You can now proceed with investment recommendations.');
            }}>Continue</button>
          </div>
        </div>
      ) : (
        <div className="question-card">
          <div className="question-header">
            <p className="section-title">SECTION {currentQ.section}: {getSectionTitle(currentQ.section)}</p>
            <h4>Question {currentQuestion + 1} of {questions.length}</h4>
            <h3>{currentQ.text}</h3>
          </div>
          <div className="options-container">
            {currentQ.options.map((option) => (
              <button
                key={option.value}
                className="option-button"
                onClick={() => handleAnswer(option.score)}
              >
                <span className="option-score">[{option.score}]</span>
                <span className="option-text">{option.label}</span>
              </button>
            ))}
          </div>
          <div className="progress-bar">
            <div 
              className="progress"
              style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
            ></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RiskProfile;